/**
 * Boolean Operators for MUI DataGrid
 *
 * Creates custom boolean operators for filtering DataGrid columns.
 * Uses the same pattern as BooleanCondition in the expression builder.
 */

import React from 'react'
import { CheckCircle, Cancel } from '@mui/icons-material'

/**
 * Create custom boolean operators for MUI DataGrid
 *
 * Following the same pattern as BooleanCondition:
 * - "Is aangevinkt" (equals true)
 * - "Is niet aangevinkt" (not equals true / equals false)
 *
 * @param {Object} intl - React Intl instance for translations
 * @returns {Array} Array of boolean operator configurations
 */
/**
 * Hidden input component that automatically sets boolean value based on operator
 * - 'equals' operator sets value to true (is checked)
 * - 'notEquals' operator sets value to false (is not checked)
 */
const BooleanInputComponent = ({ item, applyValue }) => {
  // MUI DataGrid can use either 'operator' or 'operatorValue'
  const operator = item.operator || item.operatorValue

  // Automatically set value based on operator
  React.useEffect(() => {
    // 'equals' means "is checked" (value = true)
    // 'notEquals' means "is not checked" (value = false)
    const expectedValue = operator === 'equals' ? true : false

    // Always sync value with operator
    if (item.value !== expectedValue) {
      applyValue({ ...item, value: expectedValue })
    }
  }, [operator, item, applyValue])

  return null // No visible input
}

export const createBooleanOperators = (intl) => {
  return [
    {
      value: 'equals',
      label: intl ? intl.formatMessage({ id: 'label.operator_true' }) : 'Is aangevinkt',
      requiresFilterValue: true, // Value is required but set automatically
      getApplyFilterFn: (filterItem) => {
        if (filterItem.value === undefined) return null
        return (params) => {
          // Check if value is truthy
          return Boolean(params.value) === true
        }
      },
      InputComponent: BooleanInputComponent,
    },
    {
      value: 'notEquals',
      label: intl ? intl.formatMessage({ id: 'label.operator_false' }) : 'Is niet aangevinkt',
      requiresFilterValue: true, // Value is required but set automatically
      getApplyFilterFn: (filterItem) => {
        if (filterItem.value === undefined) return null
        return (params) => {
          // Check if value is falsy
          return Boolean(params.value) === false
        }
      },
      InputComponent: BooleanInputComponent,
    }
  ]
}

/**
 * Render a boolean value as an icon
 *
 * @param {boolean} value - The boolean value to render
 * @param {Object} options - Rendering options
 * @param {boolean} options.muted - Whether to apply muted styling
 * @returns {JSX.Element} Icon element
 */
export const renderBooleanIcon = (value, options = {}) => {
  const { muted = false } = options

  const iconStyle = {
    fontSize: '1.2rem',
    opacity: muted ? 0.5 : 1,
    color: value ? '#4caf50' : '#f44336', // Green for true, red for false
  }

  return (
    <span>
      {value ? <CheckCircle sx={iconStyle} /> : <Cancel sx={iconStyle} />}
    </span>
  )
}

/**
 * Alternative: Render boolean as colored circle
 *
 * @param {boolean} value - The boolean value to render
 * @param {Object} options - Rendering options
 * @returns {JSX.Element} Circle icon element
 */
export const renderBooleanCircle = (value, options = {}) => {
  const { muted = false } = options

  const circleStyle = {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: value ? '#4caf50' : '#f44336',
    opacity: muted ? 0.5 : 1,
    marginRight: '8px'
  }

  const textStyle = {
    opacity: muted ? 0.5 : 1
  }

  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <span style={circleStyle}></span>
      <span style={textStyle}>{value ? 'Ja' : 'Nee'}</span>
    </span>
  )
}
